/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component, Fragment } from "react";
import { logErrorBoundaryInfo } from "logging/logToServer";
import AppCss from "./App.css";
import { getDefaultConfig } from "./AppConfigProp";
import { AppController } from "./AppController";
import { Provider } from "mobx-react";
import { PreviewWrapperContainer } from "containers/preview/PreviewWrapperContainer";
import * as stores from "stores";
import { OverlayDropinsContainer } from "overlays/OverlayDropinsContainer";
import { GlobalNavHelperContainer } from "containers/topbar/GlobalNavHelperContainer";
import { SearchUIMounter } from "components/search/SearchUIMounter";
import { allApisList } from "utils/APIUtils";
import { previewEvents } from "publicApis/Events/previewEvents";
import { RHPWrapperContainer } from "containers/rhp/RHPWrapperContainer";
import ToolsBoardWrapperContainer from "containers/toolsBoard/ToolsBoardWrapperContainer";
import { isCurrentDeviceMobile } from "dc-preview-utils/src/Utils/DeviceUtils";
import { ThemeProvider } from "context/themeContext";
import RemountManager from "./RemountManager";

let MsalWrapperContainer = null;
if (SHOULD_ENABLE_MSAL) {
  MsalWrapperContainer = require("containers/mip/MsalWrapperContainer").MsalWrapperContainer;
}

const defaultConfig = getDefaultConfig();

const getProps = props => {
  const { config, ...rest } = props;
  return Object.assign({}, { config: Object.assign({}, defaultConfig, config) }, rest);
};

class App extends Component {
  constructor(props) {
    super(props);
    const currentProps = getProps(props);
    this.controller = new AppController(stores, currentProps);
  }

  getApis(clientName, requiredApis) {
    return this.controller.getExposedApis(clientName, requiredApis);
  }

  get events() {
    return previewEvents;
  }

  getPublicApis(clientName, requiredApis = allApisList) {
    return this.controller.getExposedApis(clientName, requiredApis, true);
  }

  componentDidMount() {
    const unMountPromise = RemountManager.isUnmounting ? RemountManager.waitForUnmount() : Promise.resolve();
    unMountPromise.finally(() => {
      this.controller.init();
      window.previewDropinMounted = true;
    });
  }

  async componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.config.assetUri &&
      this.props.config.assetUri &&
      prevProps.config.assetUri !== this.props.config.assetUri
    ) {
      if (!this.props.config.isPreviewSwitching) await this.controller.resetAssetURI(this.props);
      else this.updateControllerProps(this.props);
    }
  }

  updateControllerProps(props) {
    stores.appStore.appController.props = getProps({ ...stores.appStore.appController.props, ...props });
  }

  componentWillUnmount() {
    this.controller.cleanup();
    window.previewDropinMounted = false;
  }

  componentDidCatch(error, info) {
    logErrorBoundaryInfo("%O", {
      componentStack: info.componentStack,
      errorStack: error.stack
    });
  }

  render() {
    const props = getProps(this.props);
    const { shouldUseMobileModernViewer } = props.config;
    if (!MsalWrapperContainer) {
      MsalWrapperContainer = React.Fragment;
    }
    return (
      <Fragment>
        <Provider {...stores}>
          <MsalWrapperContainer>
            <ThemeProvider>
              <div className={AppCss.App}>
                <GlobalNavHelperContainer intl={this.props.intl} />
                {shouldUseMobileModernViewer ? <ToolsBoardWrapperContainer /> : null}
                <PreviewWrapperContainer {...props} />
                <OverlayDropinsContainer />
                <SearchUIMounter />
                {!isCurrentDeviceMobile() && !shouldUseMobileModernViewer ? (
                  <RHPWrapperContainer intl={this.props.intl} />
                ) : null}
              </div>
            </ThemeProvider>
          </MsalWrapperContainer>
        </Provider>
      </Fragment>
    );
  }
}

App.defaultProps = {
  config: defaultConfig,
  scrollEventObj: null,
  appConfig: {},
  UIControlsConfig: {},
  documentOpenState: {},
  dcapiConfig: {},
  featureConfig: {},
  displayParams: {
    pageIndex: 0,
    x: 0,
    y: 0
  },
  /*
    a prop that checks if we need to set a state passed from outside or
    stick to an internally generated state
    */
  useDisplayParamsForOpen: false
};

export default App;
